import React, { useEffect, useState, useRef } from 'react'
import LogoWhiteWebp from '../../assets/images/webp/logo_intertennis_white.webp'
import LogoColor from '../../assets/images/webp/logo_intertennis_color.webp'
import LogoBall from '../../assets/images/logo_tilted_transparent_128.png'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../context/ThemeContext'
import TennisAnalyticsVisual from './TennisAnalyticsVisual.jsx'
import './iPadFrame.css'
import './heroVideoOverlay.css'
import { motion, AnimatePresence } from 'framer-motion'
import { Helmet } from 'react-helmet'

const smoothEase = [0.22, 1, 0.36, 1];

const HeroSection = ({ setShowSignUpPopup }) => {

    const { t } = useTranslation()
    const { isDarkMode } = useTheme()
    const [mobileVideoLoaded, setMobileVideoLoaded] = useState(false);
    const [loadingTextIndex, setLoadingTextIndex] = useState(0);

    const loadingWords = ['Loading..', 'Bouncing..', 'Slicing..', 'Hitting..', 'Preparing..', 'Rallying..', 'Serving..'];

    const videoRef = useRef(null);
    const landscapeVideoRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const patentPanelRef = useRef(null);
    const usersPanelRef = useRef(null);
    const progressBarRef = useRef(null);
    const bannerRef = useRef(null);
    const count1500Ref = useRef(null);
    const count50Ref = useRef(null);
    // Desktop activation flags
    const patentActivated = useRef(false);
    const usersActivated = useRef(false);
    // Mobile scroll story refs
    const mobileScrollRef = useRef(null);
    const mobilePatentPanelRef = useRef(null);
    const mobileUsersPanelRef = useRef(null);
    const mobileProgressBarRef = useRef(null);
    const mobileCount1500Ref = useRef(null);
    const mobileCount50Ref = useRef(null);
    const mobilePatentActivated = useRef(false);
    const mobileUsersActivated = useRef(false);

    // Count-up animation helper
    const countUp = (el, from, to, duration, suffix) => {
        const start = performance.now();
        const range = to - from;
        const step = (timestamp) => {
            const elapsed = timestamp - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            const current = Math.round(from + range * eased);
            if (el) el.textContent = current.toLocaleString() + suffix;
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    };

    const rightPaneDesc = t('hero.rightPane.descriptions', { returnObjects: true })
    // eslint-disable-next-line no-unused-vars
    const rightPaneDescWords = rightPaneDesc.map((des) => des.split(' '))

    const handleDoubleClick = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.requestFullscreen) v.requestFullscreen();
        else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
        else if (v.msRequestFullscreen) v.msRequestFullscreen();
    };

    // Mobile portrait video
    useEffect(() => {
        const video = videoRef.current;
        if (video) {
            video.src = "https://stock.inter.tennis/login_screen_videos/onboarding_demo_feb2026.mp4";
            video.loop = true;
            video.muted = true;
            video.load();
        }
    }, []);

    // Cycle loading text
    useEffect(() => {
        if (mobileVideoLoaded) return;
        const interval = setInterval(() => setLoadingTextIndex(p => (p + 1) % loadingWords.length), 1200);
        return () => clearInterval(interval);
    }, [mobileVideoLoaded, loadingWords.length]);

    // Auto-dismiss loader
    useEffect(() => {
        if (mobileVideoLoaded) return;
        const t = setTimeout(() => setMobileVideoLoaded(true), 10000);
        return () => clearTimeout(t);
    }, [mobileVideoLoaded]);

    // Desktop landscape video
    useEffect(() => {
        if (window.innerWidth < 1024) return;
        const video = landscapeVideoRef.current;
        if (video) {
            video.src = "https://stock.inter.tennis/login_screen_videos/LandscapeDemoBlueCourt27May2026.mp4";
            video.loop = true;
            video.muted = true;
            video.playbackRate = 1.5;
            video.load();
        }
    }, []);

    // ── Scroll storytelling ──
    // Stage 1: panel background fades IN over the video (JS sets panel opacity)
    // Stage 2: once dark, text slides up (is-active triggers CSS animation)
    useEffect(() => {
        if (window.innerWidth < 1024) return;

        const onScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const totalScroll = container.offsetHeight - window.innerHeight;
            const scrolled = -rect.top;
            const p = Math.max(0, Math.min(1, scrolled / totalScroll));

            // ── Patent panel opacity ──
            // Fade IN : p 0.05 → 0.14  (~34vh)
            // Hold    : p 0.14 → 0.50  (~137vh — long hold for reading)
            // Fade OUT: p 0.50 → 0.58  (~30vh)
            let patentOpacity = 0;
            if (p >= 0.05 && p < 0.14) {
                patentOpacity = (p - 0.05) / 0.09;
            } else if (p >= 0.14 && p < 0.50) {
                patentOpacity = 1;
            } else if (p >= 0.50 && p < 0.58) {
                patentOpacity = 1 - (p - 0.50) / 0.08;
            }

            // ── Users panel opacity ──
            // Fade IN : p 0.62 → 0.70  (~30vh)
            // Hold    : p 0.70 → 1.00  (~114vh — stays to end)
            let usersOpacity = 0;
            if (p >= 0.62 && p < 0.70) {
                usersOpacity = (p - 0.62) / 0.08;
            } else if (p >= 0.70) {
                usersOpacity = 1;
            }

            if (patentPanelRef.current) {
                patentPanelRef.current.style.opacity = patentOpacity;
                // Text slides up once panel is mostly dark
                if (!patentActivated.current && patentOpacity >= 0.85) {
                    patentActivated.current = true;
                    patentPanelRef.current.classList.add('is-active');
                }
                // Reset when scrolled all the way back before this panel's zone
                if (patentActivated.current && p < 0.05) {
                    patentActivated.current = false;
                    patentPanelRef.current.classList.remove('is-active');
                }
            }

            if (usersPanelRef.current) {
                usersPanelRef.current.style.opacity = usersOpacity;
                if (!usersActivated.current && usersOpacity >= 0.85) {
                    usersActivated.current = true;
                    usersPanelRef.current.classList.add('is-active');
                    // Trigger count-up on the stat numbers
                    countUp(count1500Ref.current, 0, 1500, 1400, '+');
                    countUp(count50Ref.current, 0, 50, 1100, '+');
                }
                // Reset when scrolled back before the users zone
                if (usersActivated.current && p < 0.55) {
                    usersActivated.current = false;
                    usersPanelRef.current.classList.remove('is-active');
                    // Reset display so count-up can replay
                    if (count1500Ref.current) count1500Ref.current.textContent = '0+';
                    if (count50Ref.current) count50Ref.current.textContent = '0+';
                }
            }

            // Progress bar: fills during each panel's hold zone
            if (progressBarRef.current) {
                let pct = 0;
                if (p >= 0.14 && p <= 0.50) {
                    pct = ((p - 0.14) / 0.36) * 100;
                } else if (p >= 0.70) {
                    pct = ((p - 0.70) / 0.30) * 100;
                }
                progressBarRef.current.style.width = `${Math.min(pct, 100)}%`;
                // Show bar only when a panel is active
                progressBarRef.current.style.opacity =
                    (p >= 0.14 && p <= 0.58) || p >= 0.70 ? '1' : '0';
            }

            // Headline banner: fade out as soon as user starts scrolling
            if (bannerRef.current) {
                const bannerOpacity = p < 0.06 ? 1 - p / 0.06 : 0;
                bannerRef.current.style.opacity = bannerOpacity;
                // Collapse height when invisible so it doesn't block interaction
                bannerRef.current.style.pointerEvents = bannerOpacity < 0.05 ? 'none' : '';
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // ── Mobile scroll story ──
    useEffect(() => {
        if (window.innerWidth >= 1024) return;

        const onMobileScroll = () => {
            const container = mobileScrollRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const totalScroll = container.offsetHeight - window.innerHeight;
            const scrolled = -rect.top;
            const p = Math.max(0, Math.min(1, scrolled / totalScroll));

            // Patent: fade in 0.06→0.14, hold 0.14→0.47, fade out 0.47→0.55
            let patentOpacity = 0;
            if (p >= 0.06 && p < 0.14) patentOpacity = (p - 0.06) / 0.08;
            else if (p >= 0.14 && p < 0.47) patentOpacity = 1;
            else if (p >= 0.47 && p < 0.55) patentOpacity = 1 - (p - 0.47) / 0.08;

            // Users: fade in 0.60→0.68, hold 0.68→1.0
            let usersOpacity = 0;
            if (p >= 0.60 && p < 0.68) usersOpacity = (p - 0.60) / 0.08;
            else if (p >= 0.68) usersOpacity = 1;

            if (mobilePatentPanelRef.current) {
                mobilePatentPanelRef.current.style.opacity = patentOpacity;
                if (!mobilePatentActivated.current && patentOpacity >= 0.85) {
                    mobilePatentActivated.current = true;
                    mobilePatentPanelRef.current.classList.add('is-active');
                }
                if (mobilePatentActivated.current && p < 0.04) {
                    mobilePatentActivated.current = false;
                    mobilePatentPanelRef.current.classList.remove('is-active');
                }
            }

            if (mobileUsersPanelRef.current) {
                mobileUsersPanelRef.current.style.opacity = usersOpacity;
                if (!mobileUsersActivated.current && usersOpacity >= 0.85) {
                    mobileUsersActivated.current = true;
                    mobileUsersPanelRef.current.classList.add('is-active');
                    countUp(mobileCount1500Ref.current, 0, 1500, 1400, '+');
                    countUp(mobileCount50Ref.current, 0, 50, 1100, '+');
                }
                if (mobileUsersActivated.current && p < 0.53) {
                    mobileUsersActivated.current = false;
                    mobileUsersPanelRef.current.classList.remove('is-active');
                    if (mobileCount1500Ref.current) mobileCount1500Ref.current.textContent = '0+';
                    if (mobileCount50Ref.current) mobileCount50Ref.current.textContent = '0+';
                }
            }

            // Progress bar
            if (mobileProgressBarRef.current) {
                let pct = 0;
                if (p >= 0.14 && p <= 0.52) pct = ((p - 0.14) / 0.38) * 100;
                else if (p >= 0.68) pct = ((p - 0.68) / 0.32) * 100;
                mobileProgressBarRef.current.style.width = `${Math.min(pct, 100)}%`;
                mobileProgressBarRef.current.style.opacity =
                    (p >= 0.14 && p <= 0.56) || p >= 0.68 ? '1' : '0';
            }
        };

        window.addEventListener('scroll', onMobileScroll, { passive: true });
        return () => window.removeEventListener('scroll', onMobileScroll);
    }, []);

    return (
        <section id="AG_HERO_TRUE_SECTION" className="relative">
            <Helmet>
                <link rel="preload" as="image" href={isDarkMode ? LogoWhiteWebp : LogoColor} fetchPriority="high" />
            </Helmet>

            {/* ═══════════════════════════════════════════════════════
                DESKTOP SCROLL STORY (lg+)
                300vh outer container → sticky 100vh inner wrapper
                ═══════════════════════════════════════════════════════ */}
            <div ref={scrollContainerRef} className="hidden lg:block hero-scroll-story">
                <div className="hero-scroll-sticky">

                    {/* Frosted headline strip */}
                    <div ref={bannerRef} className="hero-headline-banner" style={{ display: 'flex' }}>
                        <h1 className="hero-headline-banner__text">
                            {['hero.headline_l1', 'hero.headline_l2', 'hero.headline_l3'].map((key, i) => (
                                <motion.span
                                    key={key}
                                    className="hero-headline-banner__word"
                                    initial={{ opacity: 0, scale: 1.5, filter: 'blur(8px)' }}
                                    animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                                    transition={{ duration: 0.7, delay: 0.4 + i * 0.5, ease: [0.22, 1.2, 0.36, 1] }}
                                >
                                    {t(key)}
                                </motion.span>
                            ))}
                        </h1>
                    </div>

                    {/* Full-height video zone */}
                    <div className="hero-video-zone">

                        {/* Background video */}
                        <video
                            ref={landscapeVideoRef}
                            className="hero-video-overlay__video"
                            autoPlay loop muted playsInline preload="none"
                            onLoadedMetadata={(e) => { e.target.playbackRate = 1.5; }}
                        />

                        {/* Permanent edge vignette — frames the video */}
                        <div className="hero-video-zone__vignette" />

                        {/* Gradient scrim over video */}
                        <div className="hero-video-zone__scrim" />

                        {/* Scroll progress indicator */}
                        <div className="hero-scroll-indicator">
                            <div ref={progressBarRef} className="hero-scroll-indicator__bar" />
                        </div>

                        {/* ── PANEL 1: US Patent Pending — left/right split ── */}
                        <div ref={patentPanelRef} className="hero-scroll-panel">
                            <div className="hero-scroll-panel__split">

                                {/* Left: large heading */}
                                <div className="hero-scroll-panel__split-left">
                                    <div className="hero-scroll-panel__line" style={{ transitionDelay: '0s' }}>
                                        <p className="hero-scroll-panel__eyebrow">Technology · AI Vision</p>
                                    </div>
                                    <div className="hero-scroll-panel__line" style={{ transitionDelay: '0.18s' }}>
                                        <h2 className="hero-scroll-panel__heading">US Patent<br />Pending.</h2>
                                    </div>
                                </div>

                                {/* Vertical divider */}
                                <div className="hero-scroll-panel__vdivider hero-scroll-panel__line" style={{ transitionDelay: '0.32s' }} />

                                {/* Right: bullet points */}
                                <ul className="hero-scroll-panel__bullets">
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.48s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>Single Camera Setup</strong>
                                            <p>No special hardware required</p>
                                        </div>
                                    </li>
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.62s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>3D Pose &amp; Ball Tracking</strong>
                                            <p>Real-time AI analysis engine</p>
                                        </div>
                                    </li>
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.76s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>AI Coaching Insights</strong>
                                            <p>Personalised feedback every session</p>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>

                        {/* ── PANEL 2: Stats — TrustBand horizontal layout ── */}
                        <div ref={usersPanelRef} className="hero-scroll-panel">
                            <div className="hero-scroll-panel__content">
                                <div className="hero-scroll-panel__line">
                                    <p className="hero-scroll-panel__eyebrow">Global Community</p>
                                </div>
                                <div className="hero-scroll-panel__trust-row">
                                    <div className="hero-scroll-panel__line hero-scroll-panel__trust-metric">
                                        <span ref={count1500Ref} className="hero-scroll-panel__trust-value">1,500+</span>
                                        <span className="hero-scroll-panel__trust-label">Active Players</span>
                                    </div>
                                    <div className="hero-scroll-panel__trust-divider" />
                                    <div className="hero-scroll-panel__line hero-scroll-panel__trust-metric">
                                        <span className="hero-scroll-panel__trust-value">ITF</span>
                                        <span className="hero-scroll-panel__trust-label">Ranked Players</span>
                                    </div>
                                    <div className="hero-scroll-panel__trust-divider" />
                                    <div className="hero-scroll-panel__line hero-scroll-panel__trust-metric">
                                        <span ref={count50Ref} className="hero-scroll-panel__trust-value">50+</span>
                                        <span className="hero-scroll-panel__trust-label">Countries</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA bar — pinned to bottom */}
                        <div className="hero-video-overlay__bottom-bar">
                            <div className="hero-bottom-bar__inner">
                                <motion.div
                                    className="hero-bottom-bar__cta"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 2.0, ease: smoothEase }}
                                >
                                    <p className="hero-video-overlay__free-badge">FREE TO START · NO CARD REQUIRED</p>
                                    <button
                                        onClick={() => setShowSignUpPopup(true)}
                                        className="glow-yellow py-4 px-8 text-lg font-bold relative group overflow-hidden rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                                    >
                                        <div className="btn-background" />
                                        <span className="relative z-10 flex items-center justify-center gap-3">
                                            {t("navbar.button")}
                                            <img src={LogoBall} alt="Tennis Ball Icon" className="h-7 w-auto group-hover:rotate-12 transition-transform" />
                                        </span>
                                    </button>
                                </motion.div>
                            </div>
                        </div>

                    </div>{/* /hero-video-zone */}
                </div>{/* /hero-scroll-sticky */}
            </div>{/* /hero-scroll-story */}

            {/* ═══════════════════════════════════════════════════
                MOBILE SCROLL STORY (below lg)
                220vh outer → 100svh sticky video + overlay panels
                ═══════════════════════════════════════════════════ */}
            <div className="lg:hidden">

                {/* Sticky scroll zone */}
                <div ref={mobileScrollRef} className="mobile-scroll-story">
                    <div className="mobile-scroll-sticky">

                        {/* Loading overlay */}
                        <AnimatePresence>
                            {!mobileVideoLoaded && (
                                <motion.div className="hero-video-loader-card" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, ease: 'easeInOut' }}>
                                    <div className="loader-ball" />
                                    <AnimatePresence mode="wait">
                                        <motion.span key={loadingTextIndex} className="loader-text" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
                                            {loadingWords[loadingTextIndex]}
                                        </motion.span>
                                    </AnimatePresence>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Portrait video — fills sticky container */}
                        <video
                            ref={videoRef}
                            id="AG_HERO_TRUE_VIDEO"
                            className="mobile-scroll-video"
                            style={{ opacity: mobileVideoLoaded ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}
                            autoPlay loop muted playsInline preload="metadata"
                            onDoubleClick={handleDoubleClick}
                            onPlaying={() => setMobileVideoLoaded(true)}
                        />

                        {/* Edge vignette */}
                        <div className="hero-video-zone__vignette" />

                        {/* ── PANEL 1: Patent ── */}
                        <div ref={mobilePatentPanelRef} className="hero-scroll-panel">
                            <div className="mobile-panel__content">
                                <div className="hero-scroll-panel__line" style={{ transitionDelay: '0s' }}>
                                    <p className="hero-scroll-panel__eyebrow">Technology · AI Vision</p>
                                </div>
                                <div className="hero-scroll-panel__line" style={{ transitionDelay: '0.18s' }}>
                                    <h2 className="mobile-panel__heading">US Patent<br />Pending.</h2>
                                </div>
                                <div className="hero-scroll-panel__line" style={{ transitionDelay: '0.30s' }}>
                                    <div className="mobile-panel__hdivider" />
                                </div>
                                <ul className="mobile-panel__bullets">
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.44s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>Single Camera Setup</strong>
                                            <p>No special hardware required</p>
                                        </div>
                                    </li>
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.58s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>3D Pose &amp; Ball Tracking</strong>
                                            <p>Real-time AI analysis engine</p>
                                        </div>
                                    </li>
                                    <li className="hero-scroll-panel__line hero-scroll-panel__bullet" style={{ transitionDelay: '0.72s' }}>
                                        <span className="hero-scroll-panel__bdot" />
                                        <div>
                                            <strong>AI Coaching Insights</strong>
                                            <p>Personalised feedback every session</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* ── PANEL 2: Stats ── */}
                        <div ref={mobileUsersPanelRef} className="hero-scroll-panel">
                            <div className="mobile-panel__content">
                                <div className="hero-scroll-panel__line" style={{ transitionDelay: '0s' }}>
                                    <p className="hero-scroll-panel__eyebrow">Global Community</p>
                                </div>
                                <div className="mobile-panel__trust-row">
                                    <div className="hero-scroll-panel__line mobile-trust-metric" style={{ transitionDelay: '0.14s' }}>
                                        <span ref={mobileCount1500Ref} className="mobile-trust-value">1,500+</span>
                                        <span className="mobile-trust-label">Active Players</span>
                                    </div>
                                    <div className="mobile-panel__trust-divider" />
                                    <div className="hero-scroll-panel__line mobile-trust-metric" style={{ transitionDelay: '0.28s' }}>
                                        <span className="mobile-trust-value">ITF</span>
                                        <span className="mobile-trust-label">Ranked Players</span>
                                    </div>
                                    <div className="mobile-panel__trust-divider" />
                                    <div className="hero-scroll-panel__line mobile-trust-metric" style={{ transitionDelay: '0.42s' }}>
                                        <span ref={mobileCount50Ref} className="mobile-trust-value">50+</span>
                                        <span className="mobile-trust-label">Countries</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mobile-scroll-indicator">
                            <div ref={mobileProgressBarRef} className="mobile-scroll-indicator__bar" />
                        </div>

                    </div>
                </div>{/* /mobile-scroll-story */}

                {/* CTA panel — below scroll zone */}
                <div className="container mx-auto relative">
                    <div className='mirror-ball-left max-md:hidden'></div>
                    <div className='mirror-ball-right max-md:hidden'></div>
                    <div className="relative w-full max-w-[calc(100vw-16px)] rounded-[32px] flex justify-center items-center h-auto min-h-[480px] overflow-hidden hero-main-panel hero-right-modern-overlay">
                        <div className="w-full px-6 py-10 flex flex-col gap-6">
                            <div className="flex justify-center mb-2">
                                <img src={isDarkMode ? LogoWhiteWebp : LogoColor} alt="Inter Tennis" width={128} height={67} loading="eager" fetchPriority="high" className="w-32 h-[67px] opacity-90" />
                            </div>
                            <TennisAnalyticsVisual />
                            <div className="mt-2">
                                <p className="text-center hero-free-badge text-xs mb-2 font-semibold tracking-wide">{t('hero.free_badge')}</p>
                                <button onClick={() => setShowSignUpPopup(true)} className="glow-yellow w-full py-4 text-lg font-bold relative group overflow-hidden rounded-2xl transition-all">
                                    <div className="btn-background" />
                                    <span className="relative z-10 flex items-center justify-center gap-3">
                                        {t("navbar.button")}
                                        <img src={LogoBall} alt="Tennis Ball Icon" width={28} height={28} className="h-7 w-auto" />
                                    </span>
                                </button>
                                <p className="text-center hero-app-subtitle text-[10px] mt-3 font-medium tracking-widest uppercase">{t('hero.store_available')}</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>{/* /lg:hidden */}

        </section>
    )
}

export default HeroSection
